import { v4 as uuidV4 } from 'uuid';

import { GlintsTrackingClient } from './GlintsTrackingClient';
import { getTrackingAttributes, trackEvent } from './trackEvent';
import {
  getGlintsTrackingIDFromCookie,
  getGlintsTrackingPageName,
  getPageViewTime,
  getTrackingPageUrl,
  setGlintsTrackingIDToCookie,
  setPageViewTime,
} from './trackingSessionUtils';
import { PreviousPageInfo } from './types';

function refreshGlintsTrackingID() {
  if (!getGlintsTrackingIDFromCookie()) {
    setGlintsTrackingIDToCookie(uuidV4());
  }
}

function getReferrerPageInfo(prevPageInfo: PreviousPageInfo) {
  return {
    prev_page_path: prevPageInfo?.pagePath ?? null,
    prev_page_name: prevPageInfo?.pageName ?? null,
    prev_page_view_time: prevPageInfo?.pageViewTime ?? null,
    referrer_page: prevPageInfo?.pageName ?? null,
    referrer_page_view_time: prevPageInfo?.pageViewTime ?? null,
  };
}

function getCommonUserProperties() {
  const trackingClient = GlintsTrackingClient.getInstance();
  const userProperties = trackingClient.getCompanyProperties;
  if (!userProperties) {
    return {};
  }

  return {
    company_info: {
      company_id: userProperties.companyId,
    },
  };
}

const defaultPageNamesToIgnore = ['manage_candidates', 'signup'];

export function trackPageViewedEvent(options?: {
  pageName?: string; // if not provided, it will be inferred from the document
  pageNamesToIgnore?: string[];
  properties?: Record<string, unknown>;
  bypassSamePageCheck?: boolean;
}) {
  if (!window.GlintsTracking?.syncConfig) return;

  const trackingClient = GlintsTrackingClient.getInstance();
  const urlObject = getTrackingPageUrl();
  const pageName = options?.pageName || getGlintsTrackingPageName(document);
  const pageNamesToIgnore =
    options?.pageNamesToIgnore || defaultPageNamesToIgnore;

  if (!pageName || pageNamesToIgnore.includes(pageName)) {
    if (!pageName) {
      trackingClient.setPreviousPageInfo(null);
    }
    return;
  }
  const prevPageInfo = trackingClient.getPreviousPageInfo;

  // to handle cases where the same page component is mounted multiple times
  // ex: Suspense: when a component is suspended it unmounts and mounts the children again
  // causing the effects to fired again
  if (pageName === prevPageInfo?.pageName && !options?.bypassSamePageCheck) {
    return;
  }

  const pageViewTime = setPageViewTime(pageName);

  refreshGlintsTrackingID();

  trackEvent({
    event_type: 'page_viewed',
    properties: {
      session_id: getGlintsTrackingIDFromCookie(),
      ...getCommonUserProperties(),
      ...(prevPageInfo ? getReferrerPageInfo(prevPageInfo) : {}),
      page_path: urlObject.pathname,
      page_url: urlObject.href,
      page: pageName,
      page_view_time: pageViewTime,
      ...(options?.properties || {}),
    },
  });
  trackingClient.setPreviousPageInfo({
    pageName: pageName,
    pageViewTime: pageViewTime,
    pagePath: urlObject.pathname,
  });
}

type ManualTrackingOptions = {
  elementName: string;
  properties?: Record<string, unknown>;
  pageName?: string;
  useTargetPageName?: boolean;
};

export function trackClickEvent(target: HTMLElement | ManualTrackingOptions) {
  const pageName =
    'elementName' in target && target?.useTargetPageName
      ? target.pageName
      : getGlintsTrackingPageName(document) ??
        (target instanceof HTMLElement ? null : target.pageName);

  if (!pageName) return;

  const { element, elementName } =
    target instanceof HTMLElement
      ? {
          elementName: target
            .closest('[data-glints-tracking-element-name]')
            ?.getAttribute('data-glints-tracking-element-name') as
            | string
            | null,
          element: target.closest(
            '[data-glints-tracking-element-name]'
          ) as HTMLElement | null,
        }
      : {
          elementName: target.elementName,
          element: target.properties,
        };

  if (!elementName) return;

  refreshGlintsTrackingID();
  const urlObject = getTrackingPageUrl();
  const trackingClient = GlintsTrackingClient.getInstance();
  const prevPageInfo = trackingClient.getPreviousPageInfo;

  trackEvent({
    event_type: 'element_click',
    properties: {
      session_id: getGlintsTrackingIDFromCookie(),
      ...getCommonUserProperties(),
      ...(prevPageInfo ? getReferrerPageInfo(prevPageInfo) : {}),
      page_path: urlObject.pathname,
      page_url: urlObject.href,
      page: pageName,
      page_view_time: getPageViewTime(pageName, window.sessionStorage),
      element_name: elementName,
      ...(element instanceof HTMLElement
        ? getTrackingAttributes(element)
        : element || {}),
    },
  });
}

export function trackModalViewedEvent(
  modalName: string,
  properties?: Record<string, unknown>
) {
  refreshGlintsTrackingID();
  const pageName = getGlintsTrackingPageName(document);
  const pageViewTime = pageName ? getPageViewTime(pageName) : null;
  const urlObject = getTrackingPageUrl();

  const prevPageInfo = GlintsTrackingClient.getInstance().getPreviousPageInfo;

  const modalViewTime =
    modalName === pageName
      ? getPageViewTime(pageName)
      : setPageViewTime(modalName);

  trackEvent({
    event_type: 'page_viewed',
    properties: {
      ...properties,
      ...(prevPageInfo ? getReferrerPageInfo(prevPageInfo) : {}),
      ...getCommonUserProperties(),
      session_id: getGlintsTrackingIDFromCookie(),
      referrer_page: pageName,
      referrer_page_view_time: pageViewTime,
      page: modalName,
      page_view_time: modalViewTime,
      page_path: urlObject.pathname,
      page_url: urlObject.href,
    },
  });
}

export function trackElementImpression(target: HTMLElement) {
  const pageName = getGlintsTrackingPageName(document);
  if (!pageName) return;

  const elementName = target.getAttribute(
    'data-glints-tracking-view-element-name'
  );
  if (!elementName) return;
  trackContextEvent(elementName, getTrackingAttributes(target));
}

export function trackContextEvent(
  eventType: string,
  properties: Record<string, unknown>
) {
  const pageName = getGlintsTrackingPageName(document);
  if (!pageName) return;
  const urlObject = getTrackingPageUrl();
  refreshGlintsTrackingID();
  const trackingClient = GlintsTrackingClient.getInstance();
  const prevPageInfo = trackingClient.getPreviousPageInfo;

  trackEvent({
    event_type: eventType,
    properties: {
      session_id: getGlintsTrackingIDFromCookie(),
      ...getCommonUserProperties(),
      ...(prevPageInfo ? getReferrerPageInfo(prevPageInfo) : {}),
      page: pageName,
      page_view_time: getPageViewTime(pageName),
      page_path: urlObject.pathname,
      page_url: urlObject.href,
      ...properties,
    },
  });
}
